import { useState, useEffect } from 'react';
import { Button } from '@heroui/react';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import type Map from 'ol/Map';

interface ZoomControlsProps {
  map: Map | null;
}

export default function ZoomControls({ map }: ZoomControlsProps) {
  const [displayZoom, setDisplayZoom] = useState(1);

  useEffect(() => {
    if (!map) return;
    const view = map.getView();
    const handler = () => setDisplayZoom(view.getZoom() ?? 1);
    view.on('change:resolution', handler);
    handler();
    return () => view.un('change:resolution', handler);
  }, [map]);

  const view = map?.getView();

  const handleZoomIn = () => view?.animate({ zoom: (view.getZoom() ?? 1) + 0.5, duration: 200 });
  const handleZoomOut = () => view?.animate({ zoom: (view.getZoom() ?? 1) - 0.5, duration: 200 });
  const handleFitToView = () => {
    if (!view) return;
    const extent = view.getProjection().getExtent();
    view.fit(extent, { padding: [20, 20, 20, 20], duration: 300 });
  };

  const percentage = Math.round(Math.pow(2, displayZoom - 1) * 100);

  return (
    <div
      className="absolute bottom-4 left-4 flex items-center gap-1 rounded-lg px-1.5 py-1 z-10"
      style={{
        backgroundColor: 'var(--bg-panel)',
        border: '1px solid var(--border)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Button
        isIconOnly
        size="sm"
        variant="light"
        onPress={handleZoomOut}
        aria-label="Zoom out"
        className="min-w-7 w-7 h-7 text-[var(--text-secondary)]"
      >
        <ZoomOut size={14} />
      </Button>

      <span
        className="text-xs font-ui min-w-[3rem] text-center select-none"
        style={{ color: 'var(--text-secondary)' }}
      >
        {percentage}%
      </span>

      <Button
        isIconOnly
        size="sm"
        variant="light"
        onPress={handleZoomIn}
        aria-label="Zoom in"
        className="min-w-7 w-7 h-7 text-[var(--text-secondary)]"
      >
        <ZoomIn size={14} />
      </Button>

      <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--border)' }} />

      <Button
        isIconOnly
        size="sm"
        variant="light"
        onPress={handleFitToView}
        aria-label="Fit to viewport"
        className="min-w-7 w-7 h-7 text-[var(--text-secondary)]"
      >
        <Maximize size={14} />
      </Button>
    </div>
  );
}
